import { t, Trans } from '@lingui/macro';
import { Badge, Button, Dialog, Group, Icon } from '@economic/taco';
import ExternalLink from '~/app/shared/components/ExternalLink';
import PlanOption from '~/app/shared/components/PlanOption';
import { RackbeatPlan } from './types';
import SubscriptionPremiumDialog from './SubscriptionPremiumDialog';
import SubscriptionBasisDialog from './SubscriptionBasisDialog';

interface Props {
    children: string | JSX.Element;
    subscriptionPlan: RackbeatPlan;
    setSubscriptionPlan: (subscriptionPlan: RackbeatPlan) => void;
    trackEvent: (event: string) => void;
}

export default function Subscription({ children, subscriptionPlan, setSubscriptionPlan, trackEvent }: Props) {
    return (
        <>
            <div className="mb-2">{children}</div>
            <div className="text-center mt-6">
                <h2 className="mb-2">
                    <Trans>Choose package</Trans>
                </h2>
            </div>
            <div className="flex justify-center mb-6">
                <div className="flex justify-between flex-wrap nowrap">
                    <PlanOption
                        selected={subscriptionPlan === RackbeatPlan.Basic}
                        onSelect={() => {
                            setSubscriptionPlan(RackbeatPlan.Basic);
                            trackEvent('basis.selected');
                        }}>
                        <div>
                            <div className="flex flex-row items-start mt-4">
                                <div className="flex flex-col">
                                    <h4 className="mt-2">
                                        <Trans>Basis</Trans>
                                    </h4>
                                    <div className="mt-2 font-abel text-base font-normal leading-6 text-left">
                                        DKK 249,00/<Trans>Month</Trans>
                                    </div>
                                    <div className="mt-2 mb-2">
                                        <Trans>
                                            For those who need the basic functions for both collaboration and warehouse
                                            management.
                                        </Trans>
                                    </div>
                                    <div>
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Order management</Trans>
                                    </div>
                                    <div>
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Procurement management</Trans>
                                    </div>
                                    <div>
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Location management</Trans>
                                    </div>
                                    <div>
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Reporting</Trans>
                                    </div>
                                    <div className="flex items-center opacity-20">
                                        <Icon name="tick" className="text-blue-700" />
                                        <div>
                                            <Trans>Reorder recommendations</Trans>
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/90 to-white/50 flex justify-center p-1">
                                    <Button
                                        appearance="discrete"
                                        aria-label={t`Show full description of the basis subscription`}
                                        onClick={() => trackEvent('basis-subscription-plan-viewed')}
                                        dialog={props => (
                                            <Dialog size="xs" {...props}>
                                                <Dialog.Content aria-label="Premium subscription">
                                                    <SubscriptionBasisDialog />
                                                    <Dialog.Footer>
                                                        <Group>
                                                            <Dialog.Close>
                                                                <Button appearance="primary">Close</Button>
                                                            </Dialog.Close>
                                                        </Group>
                                                    </Dialog.Footer>
                                                </Dialog.Content>
                                            </Dialog>
                                        )}>
                                        <Trans>Show more</Trans>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </PlanOption>
                    <PlanOption
                        selected={subscriptionPlan === RackbeatPlan.Premium}
                        onSelect={() => {
                            setSubscriptionPlan(RackbeatPlan.Premium);
                            trackEvent('premium.selected');
                        }}>
                        <div>
                            <Badge className="absolute top-2 left-2" subtle color="blue">
                                <Trans>Most popular</Trans>
                            </Badge>
                            <div className="flex flex-row items-start mt-4">
                                <div className="flex flex-col">
                                    <h4 className="mt-2">
                                        <Trans>Premium</Trans>
                                    </h4>
                                    <div className="mt-2 font-abel text-base font-normal leading-6 text-left">
                                        DKK 499,00/<Trans>Month</Trans>
                                    </div>
                                    <div className="mt-2 mb-2">
                                        <Trans>
                                            For those who need advanced or specific inventory functionality – and want more
                                            options.
                                        </Trans>
                                    </div>
                                    <div>
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Everything from Basic</Trans>
                                    </div>
                                    <div className="flex items-center">
                                        <Icon name="tick" className="text-blue-700" />
                                        <div>
                                            <Trans>Production management (including bill of materials)</Trans>
                                        </div>
                                    </div>
                                    <div>
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Customer fields</Trans>
                                    </div>
                                    <div className="opacity-20">
                                        <Icon name="tick" className="text-blue-700" />
                                        <Trans>Access to the mobile app</Trans>
                                    </div>
                                </div>
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/90 to-white/50 flex justify-center p-1">
                                    <Button
                                        appearance="discrete"
                                        aria-label={t`Show full description of the premium subscription`}
                                        onClick={() => trackEvent('premium-subscription-plan-viewed')}
                                        dialog={props => (
                                            <Dialog size="xs" {...props}>
                                                <Dialog.Content aria-label="Premium subscription">
                                                    <SubscriptionPremiumDialog />
                                                    <Dialog.Footer>
                                                        <Group>
                                                            <Dialog.Close>
                                                                <Button appearance="primary">Close</Button>
                                                            </Dialog.Close>
                                                        </Group>
                                                    </Dialog.Footer>
                                                </Dialog.Content>
                                            </Dialog>
                                        )}>
                                        <Trans>Show more</Trans>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </PlanOption>
                </div>
            </div>
            <div className="mb-4">
                <Trans>
                    You also have the option of adding extra features to your subscription that suits your specific industry.
                </Trans>
            </div>
            <div className="mb-4">
                <span className="inline-block whitespace-pre-wrap">
                    <Trans>
                        Read more about prices, add-on modules and subscription types{' '}
                        <ExternalLink href="https://www.e-conomic.dk/funktioner/lager-af-rackbeat#priser">here.</ExternalLink>
                    </Trans>
                </span>
            </div>
        </>
    );
}
